import { Link } from "react-router-dom";
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { ChevronDown } from "lucide-react";
import { motion } from "framer-motion";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { useSidebar } from "@/modules/dashboard-shell/SidebarProvider";
import { cn } from "@/shared/lib/cn";
import { motionTokens, springs } from "@/shared/lib/motion";
import { useNavActive } from "../useNavActive";
import { sidebarIcons } from "../icons";

/** `pill` : le fond actif est la pastille animée (ActivePill) plutôt qu'une couleur de fond fixe. */
const navItemClass = (active: boolean, collapsed = false, pill = false) =>
  cn(
    "relative isolate flex items-center gap-3 rounded-xl text-sm font-medium transition-colors",
    // Menu replié : pastille centrée autour de l'icône.
    collapsed ? "mx-auto h-11 w-11 justify-center" : "px-4 py-3",
    active
      ? cn("font-semibold text-accent-foreground", !pill && "bg-accent shadow-sm")
      : "text-sidebar-muted hover:bg-sidebar-hover hover:text-sidebar-fg",
  );

/** Pastille lime de la page active : elle glisse d'un lien à l'autre quand on navigue. */
function ActivePill() {
  return (
    <motion.span
      layoutId="sidebar-active-pill"
      className="absolute inset-0 -z-10 rounded-xl bg-accent shadow-sm"
      transition={springs.snappy}
      aria-hidden="true"
    />
  );
}

type NavLinkProps = {
  item: NavItem;
};

function NavChildLink({ child }: { child: NavChild }) {
  const { t } = useTranslation();
  const { close } = useSidebar();
  const { isEntryActive } = useNavActive();
  const isActive = isEntryActive(child.id, child.href);
  const label = t.safecheck.nav[child.id as SafecheckNavKey];

  return (
    <Link
      to={child.href}
      onClick={() => {
        if (window.innerWidth < 1024) close();
      }}
      className={cn(
        "relative isolate flex items-center rounded-xl py-2.5 pl-11 pr-3 text-sm transition-colors",
        isActive ? "font-semibold text-accent-foreground" : "text-sidebar-muted hover:bg-sidebar-hover hover:text-sidebar-fg",
      )}
    >
      {isActive ? <ActivePill /> : null}
      {label}
    </Link>
  );
}

/** Sous-menu déplié du menu latéral : un seul à la fois (ouvrir l'un replie le précédent). */
const NavAccordionContext = createContext<{ openId: string | null; setOpenId: (id: string | null) => void } | null>(null);

export function NavAccordionProvider({ children }: { children: ReactNode }) {
  const [openId, setOpenId] = useState<string | null>(null);
  return <NavAccordionContext.Provider value={{ openId, setOpenId }}>{children}</NavAccordionContext.Provider>;
}

/** État déplié d'un sous-menu : partagé via le fournisseur, local à défaut. */
function useAccordionItem(id: string, initiallyOpen: boolean) {
  const accordion = useContext(NavAccordionContext);
  const [localOpen, setLocalOpen] = useState(initiallyOpen);
  if (!accordion) return [localOpen, setLocalOpen] as const;
  const expanded = accordion.openId === id;
  const setExpanded = (next: boolean) => accordion.setOpenId(next ? id : null);
  return [expanded, setExpanded] as const;
}

export function NavLink({ item }: NavLinkProps) {
  const { t } = useTranslation();
  const { isOpen, close, open } = useSidebar();
  const { isItemActive, isParentActive } = useNavActive();
  const { id, iconName, href, children } = item;
  const Icon = sidebarIcons[iconName];
  const label = t.safecheck.nav[id as SafecheckNavKey];
  const collapsed = !isOpen;

  const hasChildren = !!children?.length;
  const isActive = isItemActive(item);
  const parentOpen = hasChildren && isParentActive(item);
  const [expanded, setExpanded] = useAccordionItem(id, parentOpen);

  // La page affichée appartient à ce sous-menu : il s'ouvre (et replie les autres).
  useEffect(() => {
    if (parentOpen) setExpanded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentOpen]);

  if (hasChildren) {
    return (
      <div>
        <button
          type="button"
          onClick={() => {
            // Menu replié : on le déplie pour montrer les sous-pages.
            if (collapsed) {
              open();
              setExpanded(true);
              return;
            }
            setExpanded(!expanded);
          }}
          title={collapsed ? label : undefined}
          className={cn(!collapsed && "w-full", navItemClass(parentOpen, collapsed))}
        >
          <Icon size={20} className="shrink-0" />
          <span className={collapsed ? "sr-only" : "flex-1 text-left"}>{label}</span>
          {!collapsed && (
            <ChevronDown
              size={16}
              className={cn("transition-transform", expanded && "rotate-180")}
            />
          )}
        </button>
        {expanded && !collapsed && (
          <motion.div
            className="mt-1 space-y-0.5"
            initial={{ opacity: 0, y: -motionTokens.distance.xs }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: motionTokens.duration.fast, ease: motionTokens.easing.smooth }}
          >
            {children.map((child) => (
              <NavChildLink key={child.id} child={child} />
            ))}
          </motion.div>
        )}
      </div>
    );
  }

  return (
    <Link
      to={href!}
      onClick={() => {
        if (window.innerWidth < 1024) close();
      }}
      title={collapsed ? label : undefined}
      className={navItemClass(isActive, collapsed, true)}
    >
      {isActive ? <ActivePill /> : null}
      <Icon size={20} className="shrink-0" />
      <span className={collapsed ? "sr-only" : undefined}>{label}</span>
    </Link>
  );
}
